import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { CaretDown } from 'phosphor-react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'

import { useTranslation } from '../contexts/TranslationContext'
import { type Locale } from '../i18n/I18nProvider'

interface Language {
  locale: Locale
  name: string
}

enum Colors {
  DARK = 'dark',
  LIGHT = 'light',
}

interface SelectProps {
  color?: `${Colors}`
}

export const SelectLanguage = ({ color }: SelectProps) => {
  const [locale, setLocale] = useTranslation()
  const { formatMessage } = useIntl()

  const languages: Language[] = [
    {
      locale: 'en',
      name: formatMessage({ id: 'ENGLISH' }),
    },
    {
      locale: 'pt',
      name: formatMessage({ id: 'PORTUGUESE' }),
    },
    {
      locale: 'es',
      name: formatMessage({ id: 'SPANISH' }),
    },
  ]

  const currentLanguage = languages.find(
    (language) => language.locale === locale
  )

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        asChild
        className={clsx('h-10 px-4 rounded-lg border', {
          'border-grey-500': color === 'dark',
          'lg:border-grey-300': color === 'light',
        })}
      >
        <button className="flex items-center outline-none w-[146px] justify-between">
          <span
            className={clsx(
              'text-sm font-medium mr-4 lg:text-base text-grey-800',
              {
                'lg:text-grey-300': color === 'light',
              }
            )}
          >
            {currentLanguage?.name}
          </span>
          <CaretDown
            className={clsx('', {
              'lg:text-grey-300': color === 'light',
            })}
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content className="z-50 bg-white mt-1 rounded-lg py-1 border-[#cbcbcb] border w-[146px]">
          {languages
            .filter(({ locale }) => currentLanguage?.locale === locale)
            .map(({ locale, name }) => (
              <DropdownMenu.Item
                key={locale}
                className="outline-none"
                onClick={() => {
                  setLocale(locale)
                }}
              >
                <button className="w-full text-left py-3 px-4 font-medium bg-secondary-dark text-grey-100">
                  {name}
                </button>
              </DropdownMenu.Item>
            ))}
          {languages
            .filter(({ locale }) => currentLanguage?.locale !== locale)
            .map(({ locale, name }) => (
              <DropdownMenu.Item
                key={locale}
                className="outline-none"
                onClick={() => {
                  setLocale(locale)
                }}
              >
                <button className="w-full text-left py-3 px-4 text-grey-700 font-medium hover:bg-secondary-dark hover:text-grey-100">
                  {name}
                </button>
              </DropdownMenu.Item>
            ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

SelectLanguage.defaultProps = {
  color: 'dark',
}
